import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { useAssembly } from '../../context/AssemblyContext';
import { NotificationData } from '../../services/socketService';
import { Toaster, toast } from 'react-hot-toast';
import { notify } from '../../utils/notificationCenter';

// Componente principal del sistema de notificaciones
const NotificationSystem: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { joinAssembly, currentAssemblyId } = useAssembly();
  const { notifications, markNotificationAsRead } = useSocket();
  const shownNotificationIds = useRef<Set<string>>(new Set());

  const handleNotificationAction = useCallback(async (notification: NotificationData) => {
    if (notification.actionUrl) {
      const targetAssemblyId = notification.data?.targetAssemblyId || notification.data?.assembly?.id;
      const shouldOpenVotingTab = notification.data?.openVotingDetails === true;

      if (shouldOpenVotingTab && targetAssemblyId) {
        try {
          if (currentAssemblyId !== targetAssemblyId) {
            await joinAssembly(targetAssemblyId);
          }

          const destination = user?.role === 'admin'
            ? '/admin/dashboard'
            : user?.role === 'manager'
              ? '/manager/delegations'
              : user?.role === 'builder'
                ? '/builder/powers'
                : '/dashboard';

          navigate(destination, {
            state: {
              openVotingDetails: true,
              votingSessionId: notification.data?.votingSession?.id,
              targetAssemblyId,
            },
          });
        } catch (error) {
          console.error('Error al procesar navegación de notificación de asamblea:', error);
          notify({
            type: 'warning',
            priority: 'medium',
            title: 'No se pudo abrir la asamblea',
            message: 'Intenta abrirla manualmente desde el dashboard.',
          });
        }
      }
      // ✅ Manejo especial para URLs de votación
      else if (notification.actionUrl.includes('/voting/')) {
        // Navegar al tab de votación con los detalles/resultados
        // URL puede ser: /voting/ID o /voting/ID/results
        navigate(notification.actionUrl, { 
          state: { 
            openVotingDetails: true,
            votingSessionId: notification.data?.votingSession?.id 
          } 
        });
      } else if (notification.actionUrl.startsWith('http')) {
        // URLs externas se abren en nueva pestaña
        window.open(notification.actionUrl, '_blank');
      } else {
        // Navegación interna normal
        navigate(notification.actionUrl);
      }

      toast.dismiss(notification.id);
    }
  }, [currentAssemblyId, joinAssembly, navigate, user?.role]);

  useEffect(() => {
    notifications
      .filter((n) => !n.read)
      .forEach((notification) => {
        if (shownNotificationIds.current.has(notification.id)) {
          return;
        }

        shownNotificationIds.current.add(notification.id);

        if (notification.actionUrl) {
          const durationMs = (notification.duration ?? 7) * 1000;
          toast(
            (t) => (
              <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold text-gray-900">{notification.title}</div>
                <div className="text-sm text-gray-700">{notification.message}</div>
                <button
                  className="self-start rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                  onClick={() => {
                    toast.dismiss(t.id);
                    void handleNotificationAction(notification);
                  }}
                >
                  {notification.actionText || 'Abrir'}
                </button>
              </div>
            ),
            {
              id: notification.id,
              duration: durationMs,
            }
          );
        } else {
          notify({
            type: notification.type,
            priority: notification.priority,
            title: notification.title,
            message: notification.message,
            autoClose: notification.autoClose,
            duration: notification.duration,
          });
        }

        markNotificationAsRead(notification.id);
      });
  }, [notifications, markNotificationAsRead, handleNotificationAction]);

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 6000,
        style: {
          background: '#ffffff',
          color: '#111827',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(15, 23, 42, 0.12)',
          maxWidth: '420px',
        },
      }}
    />
  );
};

export default NotificationSystem;